/**
 * FFmpegAF — типизированный сборщик аудиофильтров FFmpeg.
 *
 * ЗАЧЕМ ОН ЕСТЬ (жалоба #5616, 2026-08-24: «в первом предпросмотре звук вау,
 * во втором — ужас, намного хуже исходника»).
 * /audio-enhancer собирал компрессор конкатенацией строки и умножал КАЖДЫЙ
 * параметр на «силу обработки»:
 *      'acompressor=…:makeup=' + (1 * g).toFixed(1)     // g=0.5 при «Лёгкая»
 * У `makeup` в FFmpeg домен [1 … 64], и его ЕДИНИЦА — 1 («без добавочного
 * усиления»), а не 0. То есть 0.5 — это не «мягче», это НЕВАЛИДНО:
 *      Value 0.500000 for parameter 'makeup' out of range [1 - 64]
 * Граф не конфигурируется, FFmpeg падает («Error reinitializing filters! |
 * Failed to inject frame into filter network»), тул молча уходит на
 * упрощённую запасную цепочку — без loudnorm, без корректирующих вырезов,
 * зато с четырьмя подъёмами эквалайзера в лимитер — и выдаёт результат ХУЖЕ
 * исходника. Замер 24.08 живым ffmpeg по всем комбинациям: невалидны 51 из
 * 153 уникальных цепочек — то есть ВСЕ цепочки силы «Лёгкая», во всех
 * четырёх пресетах, всегда. 501 экспорт за 30 дней, 282 события attempt2.
 *
 * Урок, который и превращён в модуль: **множитель интенсивности нельзя
 * применять к параметру, не зная его домена и его единицы.** Поэтому домен
 * объявлен рядом с параметром, а не живёт в голове автора цепочки:
 * `scale()` масштабирует ОТ ЕДИНИЦЫ, `build()` зажимает и сообщает о зажиме,
 * `violations()` даёт сторожу falsifiable-ответ БЕЗ запуска ffmpeg.
 *
 * Домены сняты с живого `ffmpeg -h filter=<name>` (7.0, 2026-08-24). Каждое
 * значение валидно и в 5.1.2, на котором собран наш WASM: сверено — ни один
 * из перечисленных диапазонов между 5.1 и 7.0 не СУЖАЛСЯ.
 */
(function (global) {
    'use strict';

    /* opt: [min, max, identity|null].
       identity — значение, при котором параметр НИЧЕГО не делает. Именно от
       него, а не от нуля, масштабирует scale(). Там, где «ничего не делать»
       числом не выражается (частота среза, время атаки), identity = null и
       scale() к параметру неприменим — он вернётся к простому умножению. */
    var SPECS = {
        acompressor: {
            level_in: [0.015625, 64, 1], threshold: [0.000976563, 1, 1],
            ratio: [1, 20, 1], attack: [0.01, 2000, null], release: [0.01, 9000, null],
            makeup: [1, 64, 1], knee: [1, 8, null], mix: [0, 1, 1], level_sc: [0.015625, 64, 1]
        },
        agate: {
            level_in: [0.015625, 64, 1], range: [0, 1, 1], threshold: [0, 1, 0],
            ratio: [1, 9000, 1], attack: [0.01, 9000, null], release: [0.01, 9000, null],
            makeup: [1, 64, 1], knee: [1, 8, null]
        },
        alimiter: {
            level_in: [0.015625, 64, 1], level_out: [0.015625, 64, 1],
            limit: [0.0625, 1, 1], attack: [0.1, 80, null], release: [1, 8000, null],
            asc_level: [0, 1, null]
        },
        equalizer: {
            f: [0, 999999, null], frequency: [0, 999999, null],
            w: [0, 99999, null], width: [0, 99999, null],
            g: [-900, 900, 0], gain: [-900, 900, 0],
            t: [1, 5, null], width_type: [1, 5, null], m: [0, 1, 1], mix: [0, 1, 1]
        },
        highpass: {
            f: [0, 999999, null], frequency: [0, 999999, null],
            w: [0, 99999, null], width: [0, 99999, null],
            p: [1, 2, null], poles: [1, 2, null], m: [0, 1, 1], mix: [0, 1, 1],
            t: [1, 5, null], width_type: [1, 5, null]
        },
        afftdn: {
            nr: [0.01, 97, 0.01], noise_reduction: [0.01, 97, 0.01],
            nf: [-80, -20, null], noise_floor: [-80, -20, null],
            rf: [-80, -20, null], residual_floor: [-80, -20, null],
            ad: [0, 1, null], adaptivity: [0, 1, null],
            gs: [0, 50, 0], gain_smooth: [0, 50, 0]
        },
        dynaudnorm: {
            f: [10, 8000, null], framelen: [10, 8000, null],
            g: [3, 301, null], gausssize: [3, 301, null],
            p: [0, 1, null], peak: [0, 1, null],
            m: [1, 100, 1], maxgain: [1, 100, 1],
            r: [0, 1, 0], targetrms: [0, 1, 0],
            s: [0, 30, 0], compress: [0, 30, 0], t: [0, 1, 0], threshold: [0, 1, 0],
            o: [0, 1, 0], overlap: [0, 1, 0]
        },
        loudnorm: {
            I: [-70, -5, null], i: [-70, -5, null],
            LRA: [1, 50, null], lra: [1, 50, null],
            TP: [-9, 0, null], tp: [-9, 0, null],
            measured_I: [-99, 0, null], measured_i: [-99, 0, null],
            measured_LRA: [0, 99, null], measured_lra: [0, 99, null],
            measured_TP: [-99, 99, null], measured_tp: [-99, 99, null],
            measured_thresh: [-99, 0, null], offset: [-99, 99, null]
        },
        stereotools: {
            level_in: [0.015625, 64, 1], level_out: [0.015625, 64, 1],
            mlev: [0.015625, 64, 1], slev: [0.015625, 64, 1],
            sbal: [-1, 1, 0], mpan: [-1, 1, 0], base: [-1, 1, 0],
            delay: [-20, 20, 0], sclevel: [1, 100, 1], phase: [0, 360, 0]
        },
        adeclick: { w: [10, 100, null], o: [50, 95, null], a: [1, 100, null] },
        adeclip: { w: [10, 100, null], o: [50, 95, null], a: [1, 100, null], n: [1, 1000, null] },
        /* Фильтры без числовых доменов, которые встречаются в наших цепочках —
           перечислены явно, чтобы violations() отличал «проверено, чисто» от
           «фильтр незнаком». */
        volume: {}, aresample: {}, asetpts: {}, anull: {}
    };
    SPECS.lowpass = SPECS.highpass;
    SPECS.sidechaincompress = SPECS.acompressor;

    function specOf(filter, opt) {
        var s = SPECS[filter];
        return (s && Object.prototype.hasOwnProperty.call(s, opt)) ? s[opt] : null;
    }

    /* FFmpeg сам понимает суффикс dB у double-параметров (threshold=-18dB →
       10^(-18/20)). Чтобы домен проверялся ПО ТОМУ ЖЕ числу, которое увидит
       фильтр, разворачиваем суффикс здесь. */
    function numeric(value) {
        if (typeof value === 'number') return isFinite(value) ? value : null;
        var s = String(value).trim();
        var m = /^(-?\d+(?:\.\d+)?)\s*dB$/i.exec(s);
        if (m) return Math.pow(10, parseFloat(m[1]) / 20);
        if (/^-?\d+(?:\.\d+)?$/.test(s)) return parseFloat(s);
        return null;   /* выражение / булево / строка — не числовой домен */
    }

    function clamp(filter, opt, value) {
        var sp = specOf(filter, opt);
        var n = numeric(value);
        if (!sp || n === null) return value;
        if (n < sp[0]) return sp[0];
        if (n > sp[1]) return sp[1];
        return value;
    }

    /**
     * scale(filter, opt, base, k) — «мягче/сильнее», не выходя из домена.
     * Масштабирует РАССТОЯНИЕ ОТ ЕДИНИЦЫ параметра, а не само значение:
     *      identity + (base - identity) * k
     * Для makeup (identity = 1) это даёт при k=0.5 и base=1 → 1, то есть
     * «без добавочного усиления», а не 0.5 = «невалидно». Ровно эту разницу
     * и не знал прежний код.
     */
    function scale(filter, opt, base, k) {
        var sp = specOf(filter, opt);
        var id = sp ? sp[2] : null;
        var v = (id === null || id === undefined) ? base * k : id + (base - id) * k;
        return clamp(filter, opt, Math.round(v * 1000) / 1000);
    }

    /**
     * build('acompressor', {threshold:'-18dB', ratio:2.8, makeup:1}) →
     *   'acompressor=threshold=-18dB:ratio=2.8:makeup=1'
     * Значение вне домена ЗАЖИМАЕТСЯ, и об этом сообщается через onClamp —
     * молча невалидная строка до FFmpeg больше не доезжает.
     */
    function build(filter, opts, onClamp) {
        var parts = [];
        for (var k in opts) {
            if (!Object.prototype.hasOwnProperty.call(opts, k)) continue;
            var raw = opts[k];
            if (raw === null || raw === undefined || raw === '') continue;
            var sp = specOf(filter, k);
            var n = numeric(raw);
            var out = raw;
            if (sp && n !== null && (n < sp[0] || n > sp[1])) {
                out = n < sp[0] ? sp[0] : sp[1];
                var cb = onClamp || build.onClamp;
                if (typeof cb === 'function') {
                    try { cb({ filter: filter, opt: k, was: n, now: out, min: sp[0], max: sp[1] }); } catch (_) {}
                }
            }
            parts.push(k + '=' + out);
        }
        return parts.length ? filter + '=' + parts.join(':') : filter;
    }
    build.onClamp = null;

    /**
     * violations('a=b=1,c=d=2') → [{filter, opt, value, num, min, max}]
     * Разбирает готовую строку -af и возвращает параметры вне домена.
     * Сторож и dev-проверка зовут ЭТО вместо запуска ffmpeg: чтобы поймать
     * makeup=0.5, декодировать звук не нужно.
     */
    function violations(chain) {
        var out = [];
        if (!chain) return out;
        String(chain).split(',').forEach(function (link) {
            link = link.trim();
            if (!link) return;
            var eq = link.indexOf('=');
            if (eq === -1) return;
            var name = link.slice(0, eq);
            if (!SPECS[name]) return;                     /* фильтр не в реестре — не наше дело */
            link.slice(eq + 1).split(':').forEach(function (pair) {
                var i = pair.indexOf('=');
                if (i === -1) return;                     /* позиционный аргумент — пропускаем */
                var opt = pair.slice(0, i), val = pair.slice(i + 1);
                var sp = specOf(name, opt);
                if (!sp) return;
                var n = numeric(val);
                if (n === null) return;
                if (n < sp[0] || n > sp[1]) {
                    out.push({ filter: name, opt: opt, value: val, num: n, min: sp[0], max: sp[1] });
                }
            });
        });
        return out;
    }

    /** Одна строка на нарушение — для сообщения теста и для тоста в dev. */
    function describe(v) {
        return v.filter + '=' + v.opt + '=' + v.value +
               ' → вне домена [' + v.min + ' … ' + v.max + ']';
    }

    /**
     * installGuard(FFmpegProcessor) — вторая дверь.
     *
     * Зажим у эмиттера (makeupGain в /audio-enhancer) чинит ИЗВЕСТНОЕ место.
     * Но параметры фильтров у нас считаются из ползунков ещё в /denoise,
     * /video-audio, /megaphone, /voice-compressor — и там та же ошибка выглядит
     * ровно так же: экспорт «получился», просто хуже. Поэтому проверка стоит
     * ещё и на общем проходе: перед exec смотрим строку -af/-filter:a.
     *
     * ⚠️ По умолчанию — ТОЛЬКО ДОКЛАД, без правки. Молча переписывать аргументы
     * ста семнадцати тулов из багфикса одного — это ровно тот «тихий подмен»,
     * который мы тут и чиним. Тул, который осознанно хочет автопочинку,
     * ставит window.__ffmpegAfAutofix = true.
     */
    function installGuard(FP) {
        if (!FP || typeof FP.process !== 'function' || FP.__afGuarded) return false;
        var orig = FP.process.bind(FP);
        FP.__afGuarded = true;
        FP.process = function () {
            var call = [].slice.call(arguments);
            try { call[1] = inspect(call[1]); }
            catch (_) { /* fail-open: заслон не имеет права ронять экспорт */ }
            return orig.apply(null, call);
        };
        return true;
    }

    function repair(chain) {
        return String(chain).split(',').map(function (link) {
            var eq = link.indexOf('=');
            if (eq === -1 || !SPECS[link.slice(0, eq)]) return link;
            var name = link.slice(0, eq);
            return name + '=' + link.slice(eq + 1).split(':').map(function (pair) {
                var j = pair.indexOf('=');
                if (j === -1) return pair;
                return pair.slice(0, j) + '=' + clamp(name, pair.slice(0, j), pair.slice(j + 1));
            }).join(':');
        }).join(',');
    }

    function inspect(args) {
        if (!Array.isArray(args)) return args;
        var out = args;
        for (var i = 0; i < args.length - 1; i++) {
            if (args[i] !== '-af' && args[i] !== '-filter:a') continue;
            var bad = violations(args[i + 1]);
            if (!bad.length) continue;
            var slugEl = (typeof document !== 'undefined')
                ? document.querySelector('meta[name="tool-slug"]') : null;
            try {
                global.__ae && global.__ae('feature_use', {
                    slug: (slugEl && slugEl.content) || 'unknown', action: 'ffmpeg_af_out_of_range',
                    filter: bad[0].filter, opt: bad[0].opt, value: String(bad[0].value),
                    min: bad[0].min, max: bad[0].max, count: bad.length,
                    fixed: global.__ffmpegAfAutofix ? 1 : 0
                });
            } catch (_) {}
            try { console.warn('[FFmpegAF] ' + bad.map(describe).join(' · ')); } catch (_) {}
            if (global.__ffmpegAfAutofix) {
                if (out === args) out = args.slice();
                out[i + 1] = repair(args[i + 1]);
            }
        }
        return out;
    }

    var API = {
        SPECS: SPECS, build: build, scale: scale, clamp: clamp,
        violations: violations, describe: describe, numeric: numeric,
        installGuard: installGuard
    };
    global.FFmpegAF = API;
    /* ffmpeg-processor.js грузится defer'ом перед нами, но на всякий случай
       (порядок мог поменяться) пробуем ещё раз после разбора документа. */
    if (!installGuard(global.FFmpegProcessor) && typeof document !== 'undefined') {
        document.addEventListener('DOMContentLoaded', function () {
            installGuard(global.FFmpegProcessor);
        });
    }
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
})(typeof window !== 'undefined' ? window : globalThis);
